import { ElementRef, Inject, Injectable } from '@angular/core';
import {
    Configurator,
    Conversation,
    GetConversationResponse,
    Language,
    Region,
    Message,
    NewConversationResponse,
    Role
} from '@obo-main/components/chatBot/DTO/Conversation';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@obo-main/services/common/alert/alert.service';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorage } from '@obo-main/utils/localstorage.service';
import { Constants } from '@obo-app/constants';

@Injectable({
    providedIn: 'root'
})
export class ChatService {
    public conversation: Conversation;
    public language: Language;
    public region: Region;
    public configurator: Configurator;

    private _chatbotBody: ElementRef;

    constructor(
        private alertService: AlertService,
        private translate: TranslateService,
        private http: HttpClient,
        @Inject('CHATBOT_URL') private chatBotUrl: string,
        @Inject(LocalStorage) private localStorage: Storage
    ) {}

    get messages(): Message[] {
        if (!this.conversation) {
            return [];
        }
        return this.conversation.messages;
    }

    get conversation_id(): string {
        if (!this.conversation) {
            return null;
        }
        return this.conversation.conversation_id || null;
    }

    set conversation_id(value: string) {
        this.conversation.conversation_id = value;
    }

    private _addMessage(message: string, role: Role): void {
        this.conversation.messages.push({
            role,
            content: message
        });
    }

    public addUserMessage(message: string): void {
        this._addMessage(message, 'user');
        this.scrollToBottom();
    }

    public addAssistantMessage(message: string): void {
        this._addMessage(message, 'assistant');
        this.scrollToBottom();
    }

    public addPartialAssistantMessage(message: string): void {
        let last_message = this.conversation.messages[this.conversation.messages.length - 1];
        if (last_message.role === 'assistant') {
            last_message.content += message;
        } else {
            this.addAssistantMessage(message);
        }

        this.scrollToBottom();
    }

    private scrollToBottom(): void {
        // In cases of an error, the chatbotBody might not be set
        if (!this._chatbotBody) {
            return;
        }

        // Wait for the next tick to scroll to the bottom
        setTimeout(() => {
            const messages = this._chatbotBody.nativeElement.getElementsByTagName('app-chatbot-message');
            if (messages.length > 0) {
                messages[messages.length - 1].scrollIntoView({ behavior: 'smooth' });
            }
        }, 0);
    }

    public newConversation(): Observable<NewConversationResponse> {
        this.conversation = {
            conversation_id: null,
            configurator: this.configurator,
            messages: [],
            language: this.language,
            region: this.region,
            updated_at: null
        } as Conversation;

        return this.http
            .post<NewConversationResponse>(`${this.chatBotUrl}/conversation/new-conversation`, {
                configurator: this.conversation.configurator,
                language: this.conversation.language,
                region: this.conversation.region
            })
            .pipe(
                tap((response) => {
                    this.conversation_id = response.conversation_id;
                    this.localStorage.setItem(Constants.storageTokens.chatbot.conversation_id, this.conversation_id);
                    this.localStorage.setItem(Constants.storageTokens.chatbot.configurator, this.configurator);
                    this.localStorage.setItem(Constants.storageTokens.chatbot.language, this.language);
                    this.localStorage.setItem(Constants.storageTokens.chatbot.region, this.region);
                }),
                catchError((error) => {
                    this._alertError(error);
                    return throwError(() => new Error('Failed to create new conversation'));
                })
            );
    }

    public getConversation(): Observable<Conversation> {
        this.tryToRetrieveConversationIdFromLocalStorage();

        if (!this.conversation_id) {
            return this.newConversation().pipe(
                switchMap(() => this._getConversation()),
                catchError((error) => {
                    this._alertError(error);
                    return throwError(() => new Error('Failed to get conversation'));
                })
            );
        }

        return this._getConversation();
    }

    public answerQuestion(question: string, chatbotBody: ElementRef): Observable<string> {
        this._chatbotBody = chatbotBody;

        if (!this.conversation_id) {
            return this.newConversation().pipe(switchMap(() => this._answerQuestion(question)));
        } else {
            return this._answerQuestion(question);
        }
    }

    private _answerQuestion(question: string): Observable<string> {
        this.addUserMessage(question);

        return new Observable<string>((observer) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', `${this.chatBotUrl}/conversation/${this.conversation_id}/answer-stream`);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.setRequestHeader('Accept', 'text/event-stream');

            // Set up SSE handling
            let processedText = '';
            xhr.onprogress = () => {
                if (xhr.status !== 200) {
                    this._alertError(xhr);
                    return;
                }

                // Get the new chunk of text
                const newText = xhr.responseText.substring(processedText.length);
                processedText = xhr.responseText;

                observer.next(newText);
            };

            xhr.onerror = (error) => {
                observer.error(error);
            };

            xhr.onload = () => {
                if (xhr.status === 200) {
                    observer.complete();
                } else {
                    observer.error(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
                }
            };

            xhr.send(
                JSON.stringify({
                    user_input: question,
                    current_configuration: this._getCurrConf()
                })
            );

            // Return cleanup function
            return () => {
                xhr.abort();
            };
        });
    }

    private _getConversation(): Observable<Conversation> {
        return this.http.get<GetConversationResponse>(`${this.chatBotUrl}/conversation/${this.conversation_id}`).pipe(
            map((response) => {
                return response.conversation;
            }),
            tap((conversation) => {
                this.conversation = conversation;
            })
        );
    }

    /*
     * Current conf can't be null, so we return an empty object if it is
     */
    private _getCurrConf(): any {
        let ufsConf = this.localStorage.getItem(Constants.storageTokens.ufs_configuration);
        if (!ufsConf) {
            return {};
        }
        return JSON.parse(ufsConf);
    }

    private _alertError(error: any): void {
        let message: string;
        switch (error.status) {
            case 404:
                message = 'CHATBOT_CONVERSATION_NOT_FOUND';
                break;
            case 429:
                message = 'CHATBOT_TOO_MANY_REQUESTS';
                break;
            case 503:
                message = 'CHATBOT_TEMPORARILY_UNAVAILABLE'; // Happens when DB is being updated
                break;
            default:
                message = 'CHATBOT_DEFAULT_ERROR';
                break;
        }

        this.alertService.danger(this.translate.instant(message));
    }

    private tryToRetrieveConversationIdFromLocalStorage() {
        let conversationId = this.localStorage.getItem(Constants.storageTokens.chatbot.conversation_id);
        let configurator = this.localStorage.getItem(Constants.storageTokens.chatbot.configurator);
        let language = this.localStorage.getItem(Constants.storageTokens.chatbot.language);

        // If the configurator or language has changed, and is not the same as the current configurator and language,
        // reset the conversation_id
        if (!(configurator === this.configurator && language === this.language)) {
            this.conversation = null;
            return;
        }

        if (conversationId) {
            this.conversation = {
                configurator: this.configurator,
                conversation_id: conversationId,
                language: this.language,
                region: this.region,
                messages: [],
                updated_at: null
            };
        }
    }
}
